"use client";

import React, { FC, useEffect, useRef, useActionState } from "react";
import { useTranslations } from "next-intl";

import { subscribeToHubspotAction } from "app/actions";
import { useOfficeName } from "context/OfficeContactPrefillContext";
import { useHubspotGeneralSegment } from "context/PageDataContext";
import { Modal } from "components/molecules/Modal/Modal";
import { SubmitButton } from "components/organisms/Form/fields/SubmitButton";

import { IconType } from "@types";
import s from "./EmailCollectionDialog.module.scss";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (email: string) => void;
  hubspotNewsletterSegment?: string | null;
}

export const EmailCollectionDialog: FC<Props> = ({
  open,
  onClose,
  onSubmit,
  hubspotNewsletterSegment,
}) => {
  const t = useTranslations("common");
  const officeName = useOfficeName();
  const hubspotGeneralSegment = useHubspotGeneralSegment();
  const segment = hubspotNewsletterSegment || hubspotGeneralSegment;
  const [state, formAction, isPending] = useActionState(subscribeToHubspotAction, {});
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (state.success && state.email) {
      onSubmit(state.email);
      formRef.current?.reset();
    }
  }, [state, onSubmit]);

  return (
    <Modal open={open} onClose={onClose}>
      <div className={s.Dialog}>
        <h3 className={s.Title}>{t("download-modal-title")}</h3>
        <p className={s.Description}>{t("download-modal-description")}</p>
        <form ref={formRef} action={formAction} className={s.Form}>
          <fieldset>
            {officeName && <input type="hidden" name="officeName" value={officeName} />}
            {segment && <input type="hidden" name="hubspotNewsletterSegment" value={segment} />}
            <input
              type="email"
              name="email"
              placeholder={t("fill-in-your-email-address")}
              className={s.Input}
              required
            />
            <label htmlFor="gdpr" className={s.Checkbox}>
              <input type="checkbox" name="gdpr" id="gdpr" required />
              {t("gdpr-text")}*
            </label>
          </fieldset>

          <div className={s.Submit}>
            <SubmitButton
              value={t("download")}
              disabled={isPending}
              isLoading={isPending}
              icon={IconType.DownloadIcon}
            />
          </div>
        </form>
      </div>
    </Modal>
  );
};
